import { useMemo, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  FilterBar,
  FilterField,
  Input,
  PageShell,
  Pagination,
  Select,
  Skeleton,
  type DataTableColumn,
} from '@/components/ui';
import {
  useSalesAggregationQuery,
  type SalesAggregationDateBasis,
  type SalesAggregationGroupBy,
  type SalesAggregationRow,
  type SalesAggregationSearchType,
  type SalesAggregationSort,
} from '../hooks/useSalesAggregationQuery';

/** QA-588 검색어 드롭다운(현금영수증 검색과 같은 방식). */
const SEARCH_TYPES: ReadonlyArray<{ value: SalesAggregationSearchType; label: string }> = [
  { value: 'PRODUCT_CODE', label: '상품코드' },
  { value: 'SELF_PRODUCT_CODE', label: '자체상품코드' },
  { value: 'PRODUCT_NAME', label: '상품명' },
];

const PAGE_SIZE = 20;
const won = (n: number): string => `${new Intl.NumberFormat('ko-KR').format(n)} KRW`;
const count = (n: number): string => new Intl.NumberFormat('ko-KR').format(n);

/** 로컬 Date → 'YYYY-MM-DD'. */
const toIsoDate = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * #20 매출집계 엔드포인트는 from/to 가 필수(미전송 시 500 MissingServletRequestParameterException).
 * 진입 시 최근 3개월(from=오늘-3M, to=오늘)을 기본 적용해 항상 from/to 를 전송한다(운영자 변경 가능).
 */
const defaultSalesWindow = (now: Date = new Date()): { from: string; to: string } => {
  const start = new Date(now);
  start.setMonth(start.getMonth() - 3);
  return { from: toIsoDate(start), to: toIsoDate(now) };
};

/**
 * #2 정산 상품별 매출 집계 화면 — be-core sales-aggregation 계약.
 * 기간·상품코드 필터 + 매출/수량 정렬 + 페이지네이션. 결제확정(CAPTURED)분만(BE 집계).
 * 입점사(vendor)축은 후속(order 에 vendor 차원 부재) — 안내 문구로 명시.
 */
export const SalesAggregationPage = () => {
  const { t } = useTranslation('settlement');

  // 진입 시 최근 3개월 기본 윈도우(from/to 필수 — 빈 호출 시 BE 500). 운영자 변경 가능.
  const [from, setFrom] = useState(() => defaultSalesWindow().from);
  const [to, setTo] = useState(() => defaultSalesWindow().to);
  const [searchType, setSearchType] = useState<SalesAggregationSearchType>('PRODUCT_CODE');
  const [keyword, setKeyword] = useState('');
  const [groupBy, setGroupBy] = useState<SalesAggregationGroupBy>('PRODUCT_CODE');
  const [dateBasis, setDateBasis] = useState<SalesAggregationDateBasis>('ORDERED_AT');
  const [sort, setSort] = useState<SalesAggregationSort>('salesAmountKrw');
  const [page, setPage] = useState(1);

  // from/to 는 BE 필수 — 운영자가 한쪽을 비워도 기본 윈도우로 폴백해 항상 전송(500 회피).
  const fallback = defaultSalesWindow();
  const effectiveFrom = from || fallback.from;
  const effectiveTo = to || fallback.to;
  const query = useSalesAggregationQuery({
    sort,
    page,
    size: PAGE_SIZE,
    from: effectiveFrom,
    to: effectiveTo,
    ...(keyword.trim() ? { searchType, keyword } : {}),
    groupBy,
    dateBasis,
  });
  // 기간 기준 이름 — BE 가 알려 주면(meta.dateBasis) 그대로, 없으면 주문일(BE 는 주문 생성 시각으로 거른다).
  const dateBasisLabel = (query.data?.meta.dateBasis ?? dateBasis) === 'PAID_AT' ? '결제일' : '주문일';
  const bySelfCode = groupBy === 'SELF_PRODUCT_CODE';

  const rows = query.data?.rows ?? [];
  const totalItems = query.data?.meta.pagination?.totalItems ?? 0;

  const resetFilters = () => {
    // 초기화 = 3개월 기본 윈도우 복원(빈 from/to=BE 500 회피).
    const w = defaultSalesWindow();
    setFrom(w.from);
    setTo(w.to);
    setSearchType('PRODUCT_CODE');
    setKeyword('');
    setGroupBy('PRODUCT_CODE');
    setDateBasis('ORDERED_AT');
    setSort('salesAmountKrw');
    setPage(1);
  };

  const toggleSort = (next: SalesAggregationSort) => {
    setSort(next);
    setPage(1);
  };

  const columns = useMemo<ReadonlyArray<DataTableColumn<SalesAggregationRow>>>(
    () => [
      {
        key: 'rank',
        header: t('settlement.salesAggregation.colRank', { defaultValue: '순위' }),
        width: 64,
        align: 'right',
        cell: (_row, index) => (
          <span className="tabular-nums text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {(page - 1) * PAGE_SIZE + index + 1}
          </span>
        ),
      },
      {
        key: 'productCode',
        // QA-588 — 자체상품코드 기준으로 보면 머리글·값도 자체상품코드.
        header: bySelfCode ? '자체상품코드' : t('settlement.salesAggregation.colProductCode', { defaultValue: '상품코드' }),
        width: 160,
        cell: (row) => (
          <span className="font-mono text-[12px]" style={{ color: 'var(--content-default)' }}>
            {bySelfCode ? (row.selfProductCode ?? '-') : row.productCode}
          </span>
        ),
      },
      {
        key: 'productName',
        header: t('settlement.salesAggregation.colProductName', { defaultValue: '상품명' }),
        cell: (row) => (
          <span className="font-medium" style={{ color: 'var(--content-strong)' }}>
            {row.productName}
          </span>
        ),
      },
      {
        key: 'salesAmountKrw',
        header: (
          <button
            type="button"
            onClick={() => toggleSort('salesAmountKrw')}
            className="inline-flex items-center gap-1"
            aria-label={t('settlement.salesAggregation.sortBy', {
              column: t('settlement.salesAggregation.colSalesAmount', { defaultValue: '매출액 (KRW)' }),
              defaultValue: '매출액 기준 정렬',
            })}
            data-testid="sales-sort-amount"
          >
            {t('settlement.salesAggregation.colSalesAmount', { defaultValue: '매출액 (KRW)' })}
            {sort === 'salesAmountKrw' ? <span aria-hidden="true">↓</span> : null}
          </button>
        ),
        align: 'right',
        width: 160,
        cell: (row) => (
          <span className="tabular-nums font-semibold" style={{ color: 'var(--content-strong)' }}>
            {won(row.salesAmountKrw)}
          </span>
        ),
      },
      {
        key: 'quantity',
        header: (
          <button
            type="button"
            onClick={() => toggleSort('quantity')}
            className="inline-flex items-center gap-1"
            aria-label={t('settlement.salesAggregation.sortBy', {
              column: t('settlement.salesAggregation.colQuantity', { defaultValue: '수량' }),
              defaultValue: '수량 기준 정렬',
            })}
            data-testid="sales-sort-quantity"
          >
            {t('settlement.salesAggregation.colQuantity', { defaultValue: '수량' })}
            {sort === 'quantity' ? <span aria-hidden="true">↓</span> : null}
          </button>
        ),
        align: 'right',
        width: 96,
        cell: (row) => (
          <span className="tabular-nums" style={{ color: 'var(--content-default)' }}>
            {count(row.quantity)}
          </span>
        ),
      },
      {
        key: 'orderCount',
        header: t('settlement.salesAggregation.colOrderCount', { defaultValue: '주문 수' }),
        align: 'right',
        width: 96,
        cell: (row) => (
          <span className="tabular-nums text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {count(row.orderCount)}
          </span>
        ),
      },
    ],
    [t, sort, page, bySelfCode],
  );

  return (
    <PageShell
      title={
        <span className="flex flex-col gap-0.5">
          <span id="sales-aggregation-heading">
            {t('settlement.salesAggregation.title', { defaultValue: '상품별 매출 집계' })}
          </span>
          <span className="text-xs font-normal text-[color:var(--content-muted)]">
            {t('settlement.salesAggregation.subject', {
              defaultValue: '기간·상품별 결제확정 매출 — 결제완료분만 집계',
            })}
          </span>
        </span>
      }
      aria-labelledby="sales-aggregation-heading"
      data-testid="sales-aggregation-page"
    >
      <FilterBar
        aria-label={t('settlement.salesAggregation.title', { defaultValue: '상품별 매출 집계' })}
        actions={
          <div className="flex items-center gap-2">
            {/* QA-195: 자동조회는 동작하나 운영자가 "검색 버튼 부재"로 미작동 오인 → 명시적
                검색 버튼 추가(현재 필터로 재조회). 입력만으로도 즉시 조회되는 동작은 유지. */}
            <Button
              variant="primary"
              size="sm"
              onClick={() => void query.refetch()}
              data-testid="sales-search"
            >
              {t('settlement.salesAggregation.search', { defaultValue: '검색' })}
            </Button>
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              {t('settlement.salesAggregation.filterReset', { defaultValue: '필터 초기화' })}
            </Button>
          </div>
        }
        filters={
          <>
            {/* QA-588 — 검색어 종류(드롭다운) + 검색어(부분 일치, 넉넉한 입력칸). */}
            <FilterField label={t('settlement.salesAggregation.filterKeyword', { defaultValue: '검색어' })}>
              <div className="flex min-w-[420px] items-center gap-2">
                <Select
                  className="w-36 shrink-0"
                  value={searchType}
                  onChange={(e) => {
                    setSearchType(e.target.value as SalesAggregationSearchType);
                    setPage(1);
                  }}
                  aria-label="검색어 종류"
                  data-testid="sales-search-type"
                >
                  {SEARCH_TYPES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
                <Input
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setPage(1);
                  }}
                  placeholder={`${SEARCH_TYPES.find((s) => s.value === searchType)?.label ?? ''} 일부만 입력해도 찾습니다`}
                  className="w-full"
                  maxLength={100}
                  aria-label="검색어"
                  data-testid="sales-keyword"
                />
              </div>
            </FilterField>
            {/* QA-588 — 기간 머리글은 지금 조회 기준(주문일/결제일)과 같게. */}
            <FilterField label={dateBasisLabel}>
              <div className="flex items-center gap-2" data-testid="sales-date-range">
                {/* order #125 — 주문일/결제일 기준 선택. 머리글은 응답 meta.dateBasis 를 따른다. */}
                <Select
                  className="w-28 shrink-0"
                  value={dateBasis}
                  onChange={(e) => {
                    setDateBasis(e.target.value as SalesAggregationDateBasis);
                    setPage(1);
                  }}
                  aria-label="기간 기준"
                  data-testid="sales-date-basis"
                >
                  <option value="ORDERED_AT">주문일</option>
                  <option value="PAID_AT">결제일</option>
                </Select>
                <Input
                  type="date"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                    setPage(1);
                  }}
                  className="w-40"
                  aria-label={`${dateBasisLabel} 시작일`}
                />
                <span aria-hidden="true">~</span>
                <Input
                  type="date"
                  value={to}
                  onChange={(e) => {
                    setTo(e.target.value);
                    setPage(1);
                  }}
                  className="w-40"
                  aria-label={`${dateBasisLabel} 종료일`}
                />
              </div>
            </FilterField>
          </>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          {t('settlement.salesAggregation.vendorFollowupNote', {
            defaultValue: '입점사(Vendor)별 집계는 후속 제공 예정입니다 — 현재는 기간·상품 단위만 지원합니다.',
          })}
        </p>
        {/* QA-588 — 집계 기준 전환(상품코드 ↔ 자체상품코드). */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setGroupBy(bySelfCode ? 'PRODUCT_CODE' : 'SELF_PRODUCT_CODE');
            setPage(1);
          }}
          data-testid="sales-group-by-toggle"
        >
          {bySelfCode ? '상품코드 기준으로 보기' : '자체상품코드 기준으로 보기'}
        </Button>
      </div>
      {bySelfCode && rows.length > 0 && rows.every((r) => r.selfProductCode === undefined) ? (
        <p role="status" className="rounded-md p-2 text-[12px]" style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }} data-testid="sales-group-by-not-ready">
          자체상품코드 기준 집계는 서버 반영 후 볼 수 있습니다. 지금은 상품코드 기준으로 묶인 결과입니다.
        </p>
      ) : null}

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('settlement.salesAggregation.error', { defaultValue: '매출 집계를 불러오지 못했습니다.' })}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('settlement.actions.retry', { defaultValue: '다시 시도' })}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={[...columns]}
          rows={[...rows]}
          rowKey={(row) => `${bySelfCode ? (row.selfProductCode ?? '') : row.productCode}|${row.productName}`}
          stickyHeader
          emptyState={t('settlement.salesAggregation.empty', { defaultValue: '해당 기간에 매출이 없습니다.' })}
          ariaLabel={t('settlement.salesAggregation.title', { defaultValue: '상품별 매출 집계' })}
        />
      )}

      <Pagination total={totalItems} currentPage={page} pageSize={PAGE_SIZE} onPageChange={setPage} />
    </PageShell>
  );
};
